import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { backgroundReadContext } from '@app/core/http/silent-background-read.context';
import { ReadOptions } from '@app/domain/models/read-options.model';
import {
  CompanyListQuery,
  CompanyDirectoryRepository,
} from '@app/domain/ports/tpv/company-directory.repository';
import { TpvCompany } from '@app/domain/models/tpv/company-directory.model';
import { TpvCompanyDetail } from '@app/domain/models/tpv/entity-detail.model';
import { TpvPage } from '@app/domain/models/tpv/tpv-page.model';
import { CompanyDto } from '@app/data/dtos/tpv/company-directory.dto';
import { CompanyDetailResponseDto } from '@app/data/dtos/tpv/entity-detail.dto';
import { mapCompany } from '@app/data/mappers/tpv/company-directory.mapper';
import { mapCompanyDetail } from '@app/data/mappers/tpv/entity-detail.mapper';
import { toTpvPage } from '@app/data/mappers/tpv/tpv-page.mapper';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope, unwrapTpv } from '@app/data/tpv/tpv-envelope';
import { toHttpParams } from './tpv-http.util';

/** `GET /api/v1/tpv/companies/`. Auth vía interceptores estándar. */
@Injectable()
export class CompanyDirectoryRemoteDataSource extends CompanyDirectoryRepository {
  private readonly http = inject(HttpClient);

  override getCompanies(
    query: CompanyListQuery = {},
    options?: ReadOptions,
  ): Observable<TpvPage<TpvCompany>> {
    // Workaround micro TPV: `get_all=true` devuelve el set completo ya scopeado al
    // usuario. No se envía page/limit (la paginación la arma el micro y deja fuera
    // empresas); los filtros sí siguen aplicando server-side. El conteo real es
    // `data.length`, NO el `total` del body (viene inflado). Ver toTpvPage(getAll).
    const params = toHttpParams({
      get_all: true,
      // Búsqueda server-side (OR entre nombre, razón social y RFC).
      // Contrato 2026-09-29: `?search=` sustituye a `?name=` para la caja de
      // texto — el back miraba solo `name` y descartaba filas cuyo RFC coincidía.
      search: query.search,
      name: query.merchantName,
      social_name: query.socialName,
      id_company: query.idCompany,
      entity: query.entity,
      // Array → CSV automático en toHttpParams (String(['A','B']) === 'A,B').
      status: query.status,
      // `deleted=true` ⇒ solo eliminadas (el backend ignora `status`). Omitido si no aplica.
      deleted: query.deleted ? true : undefined,
    });

    // `context` solo lleva marca cuando el llamador la pidió. La pantalla de
    // Empresas lee esta MISMA url sin ella, y su 5xx sigue avisando.
    return this.http
      .get<TpvEnvelope<unknown>>(tpvUrl('companies/'), {
        params,
        context: backgroundReadContext(options),
      })
      .pipe(
        map((raw) =>
          toTpvPage<CompanyDto, TpvCompany>(raw, mapCompany, { page: 1, limit: 0, getAll: true }),
        ),
      );
  }

  /** `GET /api/v1/tpv/companies/<org_id_company>/` — detalle passthrough (path con PK entero). */
  override getById(orgIdCompany: number | string): Observable<TpvCompanyDetail> {
    return this.http
      .get<TpvEnvelope<CompanyDetailResponseDto>>(tpvUrl(`companies/${orgIdCompany}/`))
      .pipe(map((raw) => mapCompanyDetail(unwrapTpv<CompanyDetailResponseDto>(raw))));
  }
}
